import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Clock, MapPin, Phone, MessageSquare, Building2 } from 'lucide-react';
import { getJummahTimes } from '../api';

export default function JummahTimes() {
  const [jummahTimes, setJummahTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getJummahTimes()
      .then((res) => setJummahTimes(res.data))
      .catch(() => setError('Failed to load Jummah times.'))
      .finally(() => setLoading(false));
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 badge-gold mb-4">
            <Users className="w-4 h-4" />
            Friday Prayer
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Jumu'ah <span className="gradient-text">Times</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Khutbah and Jumu'ah prayer times for all mosques in Oldham every Friday
          </p>

          {/* Arabic */}
          <p className="mt-4 text-gold-400 font-arabic text-xl" style={{ direction: 'rtl' }}>
            يَٰٓأَيُّهَا ٱلَّذِينَ ءَامَنُوٓا۟ إِذَا نُودِىَ لِلصَّلَوٰةِ مِن يَوْمِ ٱلْجُمُعَةِ فَٱسْعَوْا۟ إِلَىٰ ذِكْرِ ٱللَّهِ
          </p>
          <p className="text-gray-500 text-xs mt-2 italic">
            "O you who believe! When the call is made for prayer on Friday, hasten to the remembrance of Allah." — Quran 62:9
          </p>
        </motion.div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="rounded-2xl h-32 shimmer" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-20 glass-card rounded-2xl">
            <p className="text-red-400">{error}</p>
          </div>
        ) : jummahTimes.length === 0 ? (
          <div className="glass-card rounded-2xl flex flex-col items-center justify-center py-20 text-center">
            <Users className="w-16 h-16 text-gray-600 mb-4" />
            <h3 className="text-white font-semibold text-xl mb-2">No Jumu'ah Times Available</h3>
            <p className="text-gray-400">Jumu'ah times haven't been added yet.</p>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-4"
          >
            {jummahTimes.map((entry) => (
              <motion.div
                key={entry._id}
                variants={itemVariants}
                className="glass-card-light rounded-2xl overflow-hidden hover:border-gold-500/30 transition-all"
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Mosque Info */}
                  <div className="flex-1 p-6 border-b sm:border-b-0 sm:border-r border-white/5">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-lg leading-tight">
                          {entry.mosqueId?.name}
                        </h3>
                        {entry.mosqueId?.address && (
                          <div className="flex items-start gap-1.5 text-gray-500 text-sm mt-1">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            <span>{entry.mosqueId.address}</span>
                          </div>
                        )}
                        {entry.mosqueId?.phone && (
                          <div className="flex items-center gap-1.5 text-gray-500 text-sm mt-1">
                            <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                            <a href={`tel:${entry.mosqueId.phone}`} className="hover:text-emerald-400 transition-colors">
                              {entry.mosqueId.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Times */}
                  <div className="p-6 flex flex-col justify-center gap-4 sm:min-w-64">
                    <div className="grid grid-cols-2 gap-4">
                      {entry.khutbahTime && (
                        <div className="text-center">
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
                            <Clock className="w-3 h-3" />
                            Khutbah
                          </div>
                          <div className="text-gold-400 font-bold text-xl">{entry.khutbahTime}</div>
                        </div>
                      )}
                      {entry.jummahTime && (
                        <div className="text-center">
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
                            <Clock className="w-3 h-3" />
                            Jumu'ah
                          </div>
                          <div className="text-emerald-400 font-bold text-xl">{entry.jummahTime}</div>
                        </div>
                      )}
                    </div>

                    {entry.notes && (
                      <div className="flex items-start gap-2 text-gray-400 text-sm bg-white/5 rounded-lg p-3">
                        <MessageSquare className="w-4 h-4 text-emerald-500/60 flex-shrink-0 mt-0.5" />
                        <span>{entry.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Info note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-10 glass-card rounded-xl p-4 flex items-start gap-3"
        >
          <div className="w-1 h-full min-h-8 bg-gold-500 rounded-full flex-shrink-0" />
          <p className="text-gray-400 text-sm">
            <span className="text-gold-400 font-medium">Note:</span> Jumu'ah times may vary during Ramadan and other special occasions.
            Please contact your local mosque to confirm times.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
